import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../services/api";
import { useAuth, useMessage, useTheme } from "../../global-components/src";
import { errorMessage } from "../../global-components/src/utils/text";
import StoryItem from "./story-item";
import StoryViewer from "./story-viewer";
import AddStory from "./add-story";
import { Story, UserStories } from "../../types";
import PlaceholderStory from "./placeholder-story";
import {
  markStoryAsViewed,
  orderUserStoriesByUnseen,
  hasUnseenStories,
} from "../../utils/story-viewing";

const StoriesBar: React.FC = () => {
  const { token, user } = useAuth();

  const [usersStories, setUsersStories] = useState<UserStories[]>([]);
  const [activeUserIndex, setActiveUserIndex] = useState<number | null>(null);
  const [activeStoryIndex, setActiveStoryIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const isFirstLoading = useRef(true);
  const [cursor, setCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const { isDarkMode } = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { setMessage } = useMessage();

  const loadData = async () => {
    if (loading || !hasMore) return;

    try {
      setLoading(true);

      const response = await api(token).get("/stories", {
        params: { cursor },
      });

      const grouped = response.data.data.reduce(
        (acc: Record<number, UserStories>, story: Story) => {
          if (!story?.user?.id) return acc; // 🔴 ignora totalmente

          const userId = story.user.id;

          if (!acc[userId]) {
            acc[userId] = {
              user: story.user,
              stories: [],
            };
          }

          acc[userId].stories.push(story);
          return acc;
        },
        {}
      );


      const newUsersStories: UserStories[] = Object.values(grouped);
      
     setUsersStories(prev => {
        const map = new Map<number, UserStories>();

        [...prev, ...newUsersStories].forEach(item => {
          map.set(item.user.id, item);
        });

        const merged = Array.from(map.values());
        return activeUserIndex === null ? orderUserStoriesByUnseen(merged, user?.id) : merged;
      });

      // setUsersStories(prev => [...prev, ...Object.values(grouped)]);
      setCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (err: any) {
      console.error(err);
      console.error(errorMessage(err));
      if(err?.response?.data?.reason)
        setMessage(err.response?.data.message || "Erro ao carregar stories.", "error", undefined, undefined, err.response?.data.reason);
    } finally {
      isFirstLoading.current = false;
      setLoading(false);
    }
  };

  const handleStoryAdded = (newStory: Story) => {
    if (!newStory?.user?.id) return;

    const ownStory = { ...newStory, has_viewed: true };
    setUsersStories(prev => {
      const existing = prev.find(u => u.user.id === ownStory.user.id);

      if (existing) {
        // já tem stories desse usuário → adiciona no início
        return prev.map(u =>
          u.user.id === ownStory.user.id
            ? { ...u, stories: [ownStory, ...u.stories].slice(0, 5) }
            : u
        );
      }

      // usuário novo → coloca no início da lista
      return [{ user: ownStory.user, stories: [ownStory] }, ...prev];
    });
  };

  const handleStoryViewed = useCallback((storyId: number) => {
    setUsersStories(prev => markStoryAsViewed(prev, storyId));
  }, []);

  const closeViewer = useCallback(() => {
    setActiveUserIndex(null);
    setUsersStories(prev => orderUserStoriesByUnseen(prev, user?.id));
  }, [user?.id]);

  const handleStoryDeleted = (storyId: number, userId: number) => {
    setUsersStories(prev => {
      return prev
        .map(u =>
          u.user.id === userId
            ? { ...u, stories: u.stories.filter(s => s.id !== storyId) }
            : u
        )
        .filter(u => u.stories.length > 0); // remove usuário se ficar sem stories
    });
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      const nearEnd =
      el.scrollLeft + el.clientWidth >= el.scrollWidth - 50;
      
      if (nearEnd) {
        loadData();
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [cursor, hasMore, loading]);



  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (
      window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.postMessage === "function"
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          statusBarStyle: activeUserIndex !== null ? "#000" : "var(--bg-row-type-feed)",
        })
      );
    }
  }, [activeUserIndex]);

  return (
    <>
        <>
          <div className="card card-post card card-story">
            <div ref={containerRef} className="stories-bar d-flex gap-3 overflow-auto p-2 scroll-none">

              <AddStory onStoryAdded={handleStoryAdded}/>

              {usersStories.map((item, index) => (
                <StoryItem
                  key={item.user.id}
                  userStories={item}
                  hasUnseenStory={hasUnseenStories(item)}
                  onClick={() => {
                    setActiveUserIndex(index);
                    setActiveStoryIndex(0);
                  }}
                />
              ))}

              {[...Array(10)].map((item, index) => (
                <PlaceholderStory loading={loading || isFirstLoading.current}/>
              ))}
            </div>

            {activeUserIndex !== null && (
              <StoryViewer
                usersStories={usersStories}
                userIndex={activeUserIndex}
                storyIndex={activeStoryIndex}
                setUserIndex={setActiveUserIndex}
                setStoryIndex={setActiveStoryIndex}
                onClose={closeViewer}
                onLoadMore={loadData}
                onStoryDeleted={handleStoryDeleted}
                onStoryViewed={handleStoryViewed}
              />
            )}
          </div>
        </>
    </>
  );
};

export default StoriesBar;
